import { useEffect } from "react";
import { useStore } from "../../store";
import { translateLiteral } from "../../lib/i18n";

const originals = new WeakMap();
const ATTRS = ["placeholder", "title", "aria-label"];

function translateNode(node, language) {
  if (node.nodeType === Node.TEXT_NODE) {
    if (!originals.has(node)) originals.set(node, node.nodeValue);
    const original = originals.get(node);
    const translated = translateLiteral(language, original);
    if (node.nodeValue !== translated) node.nodeValue = translated;
    return;
  }

  if (node.nodeType !== Node.ELEMENT_NODE) return;
  const tag = node.tagName?.toLowerCase();
  if (tag === "script" || tag === "style") return;

  for (const attr of ATTRS) {
    if (!node.hasAttribute(attr)) continue;
    const key = `data-i18n-original-${attr}`;
    if (!node.hasAttribute(key)) node.setAttribute(key, node.getAttribute(attr));
    const translated = translateLiteral(language, node.getAttribute(key));
    if (node.getAttribute(attr) !== translated) node.setAttribute(attr, translated);
  }

  node.childNodes.forEach((child) => translateNode(child, language));
}

export default function I18nDomTranslator() {
  const language = useStore((s) => s.currentUser?.language ?? "fr");

  useEffect(() => {
    const root = document.body;
    translateNode(root, language);

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => translateNode(node, language));
        // React often updates text in place; refresh the French "source" we cache
        // so the panel does not keep showing a stale name after selection changes.
        if (mutation.type === "characterData" && mutation.target.nodeType === Node.TEXT_NODE) {
          originals.set(mutation.target, mutation.target.nodeValue);
          translateNode(mutation.target, language);
        }
        if (mutation.type === "attributes") translateNode(mutation.target, language);
      }
    });

    observer.observe(root, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ATTRS,
    });

    return () => observer.disconnect();
  }, [language]);

  return null;
}
